/*ReactJS*/
import React, { useState, useContext, useMemo } from 'react';

import {
  notifySuccess,
  notifyError,
} from 'components/Notification/Notification';

/*Service*/
import {
  batchCreateTransaction,
  batchCreatePendingTransaction,
  batchValidateTransactions,
  convertDataToTransactionModel,
  checkEntryType,
  BatchPartialError,
  generateExternalIdForTransaction,
} from '../../../services/TransactionService';

import { readExcelData } from '../../../services/ExcelService';

/*Material UI*/
import { AttachFile, Close as CloseIcon, Info as InfoIcon } from '@material-ui/icons';

import {
  Button,
  Modal,
  Backdrop,
  Fade,
  Box,
  IconButton,
  CircularProgress,
  Tooltip,
  Popover,
} from '@material-ui/core';

import TransactionTable from './TransactionTable';
import EntryTypeSelect from '../../../components/AutoComplete/EntryType';
import { ENTRY_TYPES_AUTOFILL_BLACKLIST } from './TransactionForm';
import {
  getEntryTypeMetadataErrors,
  affectsShortLeg,
} from './validation';

import useStyles from '../../../styles';
import { ServiceContext } from 'context/ServiceContext';
import { useUserState } from 'context/UserContext';
import { useConfirm } from 'material-ui-confirm';

const PREVIEW_ROW_LIMIT = 10000;

function BatchErrorLines(props) {
  const [showAll, setShowAll] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  
  // batchError is type-checked
  const errs = props.batchError.getErrors();
  
  // Group errors by error message
  const errorGroups = errs.reduce((acc, err) => {
    const key = err.errorMessage || 'Unknown error';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(err);
    return acc;
  }, {});
  
  const errorTypes = Object.keys(errorGroups);
  const displayLimit = 5;
  const displayedErrorTypes = showAll ? errorTypes : errorTypes.slice(0, displayLimit);
  
  // Check if there are any "External ID already exists" errors
  const hasDuplicateExternalIdError = errorTypes.some(
    (errorType) => errorType.toLowerCase().includes('external id already exists')
  );
  
  const handleInfoClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleInfoClose = () => {
    setAnchorEl(null);
  };
  
  const openInfo = Boolean(anchorEl);
  const infoPopoverId = openInfo ? 'info-popover' : undefined;
  
  return (
    <div style={{ padding: '15px 0' }}>
      <div style={{ 
        marginBottom: '15px', 
        fontWeight: 'bold', 
        fontSize: '16px',
        color: '#d32f2f',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span>📋 Error Summary: {errs.length} transaction(s) failed</span>
        {hasDuplicateExternalIdError && (
          <>
            <Tooltip title="Click for help with duplicate errors">
              <IconButton 
                size="small" 
                onClick={handleInfoClick}
                style={{ 
                  padding: '4px',
                  color: '#1976d2',
                  backgroundColor: '#e3f2fd',
                  border: '2px solid #1976d2'
                }}
                aria-describedby={infoPopoverId}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Popover
              id={infoPopoverId}
              open={openInfo}
              anchorEl={anchorEl}
              onClose={handleInfoClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <div style={{ 
                padding: '20px', 
                maxWidth: '500px',
                backgroundColor: '#fff'
              }}>
                <div style={{ 
                  fontWeight: 'bold', 
                  fontSize: '16px', 
                  marginBottom: '12px',
                  color: '#1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <InfoIcon /> Help: Duplicate External ID Errors
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.6', color: '#333' }}>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Why am I seeing this error?</strong>
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    The system detected that these transactions have External IDs that already exist in the database. 
                    This prevents duplicate transactions from being processed.
                  </p>
                  
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Are these legitimate (non-duplicate) transactions?</strong>
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    If these transactions are <u>NOT duplicates</u> and should be processed:
                  </p>
                  <ol style={{ marginLeft: '20px', marginBottom: '12px' }}>
                    <li style={{ marginBottom: '6px' }}>
                      Download the error report above
                    </li>
                    <li style={{ marginBottom: '6px' }}>
                      Add a unique <strong>&quot;External ID&quot;</strong> column value for each transaction 
                      (or leave it blank to auto-generate)
                    </li>
                    <li style={{ marginBottom: '6px' }}>
                      Re-upload the corrected Excel file
                    </li>
                  </ol>
                  
                  <div style={{ 
                    backgroundColor: '#fff3cd', 
                    padding: '12px', 
                    borderRadius: '4px',
                    borderLeft: '4px solid #ffc107',
                    marginTop: '12px'
                  }}>
                    <strong>💡 Tip:</strong> If you leave the External ID column empty, 
                    the system will automatically generate a unique ID for each transaction.
                  </div>
                </div>
              </div>
            </Popover>
          </>
        )}
      </div>
      
      {/* Show grouped errors */}
      {displayedErrorTypes.map((errorMsg) => {
        const transactions = errorGroups[errorMsg];
        const displayedTransactions = transactions.slice(0, 3);
        
        return (
          <div 
            key={errorMsg} 
            style={{ 
              marginBottom: '15px', 
              padding: '12px', 
              backgroundColor: '#fff3cd', 
              borderLeft: '4px solid #ffc107',
              borderRadius: '4px'
            }}
          >
            <div style={{ 
              fontWeight: 'bold', 
              marginBottom: '8px',
              color: '#856404'
            }}>
              {errorMsg} ({transactions.length} occurrence{transactions.length > 1 ? 's' : ''})
            </div>
            <div style={{ fontSize: '0.9em', color: '#666' }}>
              {displayedTransactions.map((txn) => (
                <div key={txn.index} style={{ marginBottom: '4px' }}>
                  • Row {txn.index}: Account: <b>{txn.accountNo}</b>
                  {txn.contraAccountNo && `, Contra: ${txn.contraAccountNo}`}
                  {txn.symbol && `, Symbol: ${txn.symbol}`}
                  {txn.externalId && `, External ID: ${txn.externalId.substring(0, 12)}...`}
                </div>
              ))}
              {transactions.length > 3 && (
                <div style={{ marginTop: '4px', fontStyle: 'italic', color: '#999' }}>
                  ...and {transactions.length - 3} more transaction(s) with this error
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      {/* Show more button */}
      {errorTypes.length > displayLimit && !showAll && (
        <button 
          onClick={() => setShowAll(true)}
          style={{ 
            marginTop: '10px',
            marginBottom: '15px',
            padding: '8px 16px',
            color: '#007bff',
            backgroundColor: 'white',
            border: '1px solid #007bff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          ▼ Show all {errorTypes.length} error types
        </button>
      )}
      
      {showAll && errorTypes.length > displayLimit && (
        <button 
          onClick={() => setShowAll(false)}
          style={{ 
            marginTop: '10px',
            marginBottom: '15px',
            padding: '8px 16px',
            color: '#007bff',
            backgroundColor: 'white',
            border: '1px solid #007bff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          ▲ Show less
        </button>
      )}
      
      {/* Download full report button */}
      <div style={{ 
        marginTop: '20px', 
        padding: '15px',
        backgroundColor: '#e3f2fd',
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        <a
          onClick={() => downloadCSV('ledgie-transaction-errors.csv', errs)}
          style={{
            color: '#007bff',
            textDecoration: 'none',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '15px'
          }}
        >
          📥 Download Full Error Report ({errs.length} records)
        </a>
        <div style={{ fontSize: '0.85em', color: '#666', marginTop: '5px' }}>
          CSV file includes all error details for easy review and correction
        </div>
      </div>
    </div>
  );
}

export default function TransactionUploadModal({
  onClose: handleClose,
  open: isOpen,
}) {
  var { googleAccount } = useUserState();
  var activeUser = googleAccount.profileObj.name;

  const classes = useStyles();
  const confirm = useConfirm();

  const [rows, setRows] = useState([]);
  const [rowData, setRowData] = React.useState({});
  const [editRecord, setEditRecord] = React.useState(false);
  const [entryType, setEntryType] = useState('');
  const [loading, setLoading] = useState({
    execute: false,
    saveToPending: false,
    validate: false,
  });
  const [batchError, setBatchError] = useState(null);

  // can preview up to the first 10000 rows of an upload in the table before performance issues occurring
  const previewRows = useMemo(() => rows.slice(0, PREVIEW_ROW_LIMIT), [rows]);

  const listReq = [];

  const readExcel = (file) => {
    if (!file) {
      return;
    }
    setRows([]);

    readExcelData(file).then((d) => {
      // Validate that the Excel sheet has headers (first row should be headers)
      if (d.length === 0) {
        notifyError('The Excel file is empty. Please upload a file with data.');
        return;
      }

      // Check if the first item is an object with string keys (indicating headers exist)
      const firstItem = d[0];
      if (!firstItem || typeof firstItem !== 'object' || Array.isArray(firstItem)) {
        notifyError(
          'Invalid Excel format. The first row must contain column headers. Please ensure your Excel sheet has proper headers.'
        );
        return;
      }

      // Check if the object has at least some expected headers
      const requiredHeaders = ['Entry Type', 'Account No', 'Contra Account No'];
      const hasRequiredHeaders = requiredHeaders.some((header) => {
        const colNameLower = header.toLowerCase();
        const foundKey = Object.keys(firstItem).find((k) => k.toLowerCase() === colNameLower);
        return foundKey !== undefined;
      });

      if (!hasRequiredHeaders) {
        notifyError(
          'Invalid Excel format. The first row must be headers with expected column names like "Entry Type", "Account No", "Contra Account No", etc. ' +
          'Please check your Excel template and ensure headers are in the first row.'
        );
        return;
      }

      let rowsCopy = [];

      d.map((item) => {
        return rowsCopy.push(convertDataToTransactionModel(item));
      });

      if (!rowsCopy.every((item) => item.error === false)) {
        notifyError(
          'Successfully load(s) ' +
            d.length +
            ' records with missing required values.'
        );
      } else {
        notifySuccess('Successfully load(s) ' + d.length + ' records.');
        setBatchError(null);
      }
      setRows(rowsCopy);
    }).catch((error) => {
      console.error('Error reading Excel file:', error);
      notifyError(
        'Failed to read Excel file. Please ensure the file is a valid Excel file (.xlsx) with headers in the first row.'
      );
    });

    return;
  };

  const { transactionServiceClient: client } = useContext(ServiceContext);

  const handleExecution = async () => {
    setLoading({ execute: true });

    try {
      const records = rows.length;
      if (records > 0) {
        const rowsCopy = [...rows];
        
        // Generate external_id for each transaction that doesn't have one
        rowsCopy.forEach((row) => {
          if (!row.externalId) {
            row.externalId = generateExternalIdForTransaction(row);
          }
        });

        if (blockOnMetadataErrors(rowsCopy)) {
          return;
        }

        const shortLegCount = rowsCopy.filter((row) =>
          affectsShortLeg(row.metadata, row.qty)
        ).length;
        if (shortLegCount > 0) {
          try {
            await confirm({
              description:
                `${shortLegCount} transaction(s) target the short leg (CAD 3) ` +
                'for unnetted accounts. Review existing positions before continuing.',
              confirmationText: 'Execute short-leg transactions',
              cancellationText: 'Cancel',
            });
          } catch {
            return;
          }
        }
        
        await Promise.all(rowsCopy.map(createBatchRequest));

        const res = await batchCreateTransaction(
          listReq,
          client,
          activeUser,
          true
        );
        
        // Aggregated results tracking
        const results = {
          total: res.length,
          executed: 0,
          deleted: 0,
          rejected: 0,
          failed: 0,
          errors: [], // Limited to first 1000 errors
          errorsByType: new Map(), // Group by error message
        };
        const MAX_STORED_ERRORS = 1000;

        if (res.length > 0) {
          res.forEach((item, index) => {
            const status = item.status ? item.status.toLowerCase() : '';
            const errorMessage = item.errorMessage ? item.errorMessage.trim() : '';
            
            if (status === 'executed') {
              results.executed++;
            } else if (status === 'deleted') {
              results.deleted++;
              results.failed++;
              
              const errorKey = errorMessage || 'Duplicate external_id';
              results.errorsByType.set(
                errorKey,
                (results.errorsByType.get(errorKey) || 0) + 1
              );
              
              if (results.errors.length < MAX_STORED_ERRORS) {
                results.errors.push({
                  index: index + 1,
                  status: 'Deleted',
                  errorMessage: errorKey,
                  accountNo: item.accountNo || rowsCopy[index]?.accountNo,
                  contraAccountNo: item.contraAccountNo || rowsCopy[index]?.contraAccountNo,
                  symbol: item.symbol || rowsCopy[index]?.symbol,
                  cusip: item.cusip || rowsCopy[index]?.cusip,
                  externalId: item.externalId || rowsCopy[index]?.externalId,
                });
              }
            } else if (status === 'rejected') {
              results.rejected++;
              results.failed++;
              
              const errorKey = errorMessage || 'Transaction rejected';
              results.errorsByType.set(
                errorKey,
                (results.errorsByType.get(errorKey) || 0) + 1
              );
              
              if (results.errors.length < MAX_STORED_ERRORS) {
                results.errors.push({
                  index: index + 1,
                  status: 'Rejected',
                  errorMessage: errorKey,
                  accountNo: item.accountNo || rowsCopy[index]?.accountNo,
                  contraAccountNo: item.contraAccountNo || rowsCopy[index]?.contraAccountNo,
                  symbol: item.symbol || rowsCopy[index]?.symbol,
                  cusip: item.cusip || rowsCopy[index]?.cusip,
                  externalId: item.externalId || rowsCopy[index]?.externalId,
                });
              }
            } else if (errorMessage !== '') {
              results.failed++;
              
              const errorKey = errorMessage;
              results.errorsByType.set(
                errorKey,
                (results.errorsByType.get(errorKey) || 0) + 1
              );
              
              if (results.errors.length < MAX_STORED_ERRORS) {
                results.errors.push({
                  index: index + 1,
                  status: status.charAt(0).toUpperCase() + status.slice(1),
                  errorMessage: errorKey,
                  accountNo: item.accountNo || rowsCopy[index]?.accountNo,
                  contraAccountNo: item.contraAccountNo || rowsCopy[index]?.contraAccountNo,
                  symbol: item.symbol || rowsCopy[index]?.symbol,
                  cusip: item.cusip || rowsCopy[index]?.cusip,
                  externalId: item.externalId || rowsCopy[index]?.externalId,
                });
              }
            }
          });
        }

        // Show aggregated summary notification
        if (results.failed === 0) {
          notifySuccess(
            `✓ Successfully executed ${results.total} transaction(s).`
          );
          rowsCopy.splice(0, rowsCopy.length - 1);
          setRows([]);
          setBatchError(null);
        } else if (results.executed > 0) {
          // Partial success - show top 2 error types
          const topErrors = Array.from(results.errorsByType.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([msg, count]) => `${count}× "${msg.substring(0, 40)}${msg.length > 40 ? '...' : ''}"`)
            .join(', ');
          
          notifyError(
            `Partial success: ${results.executed}/${results.total} executed. ` +
            `${results.failed} failed (${results.deleted} deleted, ${results.rejected} rejected). ` +
            `Common errors: ${topErrors}. See details below.`
          );
          
          setBatchError(new BatchPartialError(
            `${results.failed} transaction(s) failed`,
            results.errors
          ));
        } else {
          // Total failure
          const topError = Array.from(results.errorsByType.entries())
            .sort((a, b) => b[1] - a[1])[0];
          
          notifyError(
            `All ${results.total} transaction(s) failed. ` +
            `Most common: ${topError[1]}× "${topError[0].substring(0, 50)}${topError[0].length > 50 ? '...' : ''}". ` +
            `${results.deleted} deleted, ${results.rejected} rejected. See error report below.`
          );
          
          setBatchError(new BatchPartialError(
            `All transactions failed`,
            results.errors
          ));
        }
      }
    } catch (error) {
      console.error('Failed to execute', error);
      setBatchError(error);
    } finally {
      setLoading({ execute: false });
    }
  };

  const handlePendingExecution = async () => {
    setLoading({ saveToPending: true });

    try {
      const records = rows.length;
      if (records > 0) {
        const rowsCopy = [...rows];
        
        // Generate external_id for each transaction that doesn't have one
        rowsCopy.forEach((row) => {
          if (!row.externalId) {
            row.externalId = generateExternalIdForTransaction(row);
          }
        });

        if (blockOnMetadataErrors(rowsCopy)) {
          return;
        }
        
        await Promise.all(rowsCopy.map(createBatchRequest));

        const res = await batchCreatePendingTransaction(
          listReq,
          client,
          activeUser
        );

        // Aggregated results tracking
        const results = {
          total: res.length,
          pending: 0,
          deleted: 0,
          rejected: 0,
          failed: 0,
          errors: [], // Limited to first 1000 errors
          errorsByType: new Map(), // Group by error message
        };
        const MAX_STORED_ERRORS = 1000;

        if (res.length > 0) {
          res.forEach((item, index) => {
            const status = item.status ? item.status.toLowerCase() : '';
            const errorMessage = item.errorMessage ? item.errorMessage.trim() : '';
            
            if (errorMessage === '' && status === 'pending') {
              results.pending++;
            } else if (status === 'deleted') {
              results.deleted++;
              results.failed++;
              
              const errorKey = errorMessage || 'Duplicate external_id';
              results.errorsByType.set(
                errorKey,
                (results.errorsByType.get(errorKey) || 0) + 1
              );
              
              if (results.errors.length < MAX_STORED_ERRORS) {
                results.errors.push({
                  index: index + 1,
                  status: 'Deleted',
                  errorMessage: errorKey,
                  accountNo: item.accountNo || rowsCopy[index]?.accountNo,
                  contraAccountNo: item.contraAccountNo || rowsCopy[index]?.contraAccountNo,
                  symbol: item.symbol || rowsCopy[index]?.symbol,
                  cusip: item.cusip || rowsCopy[index]?.cusip,
                  externalId: item.externalId || rowsCopy[index]?.externalId,
                });
              }
            } else if (status === 'rejected') {
              results.rejected++;
              results.failed++;
              
              const errorKey = errorMessage || 'Transaction rejected';
              results.errorsByType.set(
                errorKey,
                (results.errorsByType.get(errorKey) || 0) + 1
              );
              
              if (results.errors.length < MAX_STORED_ERRORS) {
                results.errors.push({
                  index: index + 1,
                  status: 'Rejected',
                  errorMessage: errorKey,
                  accountNo: item.accountNo || rowsCopy[index]?.accountNo,
                  contraAccountNo: item.contraAccountNo || rowsCopy[index]?.contraAccountNo,
                  symbol: item.symbol || rowsCopy[index]?.symbol,
                  cusip: item.cusip || rowsCopy[index]?.cusip,
                  externalId: item.externalId || rowsCopy[index]?.externalId,
                });
              }
            } else if (errorMessage !== '') {
              results.failed++;
              
              const errorKey = errorMessage;
              results.errorsByType.set(
                errorKey,
                (results.errorsByType.get(errorKey) || 0) + 1
              );
              
              if (results.errors.length < MAX_STORED_ERRORS) {
                results.errors.push({
                  index: index + 1,
                  status: status.charAt(0).toUpperCase() + status.slice(1),
                  errorMessage: errorKey,
                  accountNo: item.accountNo || rowsCopy[index]?.accountNo,
                  contraAccountNo: item.contraAccountNo || rowsCopy[index]?.contraAccountNo,
                  symbol: item.symbol || rowsCopy[index]?.symbol,
                  cusip: item.cusip || rowsCopy[index]?.cusip,
                  externalId: item.externalId || rowsCopy[index]?.externalId,
                });
              }
            }
          });
          
          // Show aggregated summary notification
          if (results.failed === 0 && results.pending > 0) {
            notifySuccess(
              `✓ Successfully added ${results.pending} pending transaction(s).`
            );
            rowsCopy.splice(0, rowsCopy.length - 1);
            setRows([]);
            setBatchError(null);
          } else if (results.pending > 0) {
            // Partial success - show top 2 error types
            const topErrors = Array.from(results.errorsByType.entries())
              .sort((a, b) => b[1] - a[1])
              .slice(0, 2)
              .map(([msg, count]) => `${count}× "${msg.substring(0, 40)}${msg.length > 40 ? '...' : ''}"`)
              .join(', ');
            
            notifyError(
              `Partial success: ${results.pending}/${results.total} added to pending. ` +
              `${results.failed} failed (${results.deleted} deleted, ${results.rejected} rejected). ` +
              `Common errors: ${topErrors}. See details below.`
            );
            
            setBatchError(new BatchPartialError(
              `${results.failed} transaction(s) failed`,
              results.errors
            ));
          } else {
            // Total failure
            const topError = Array.from(results.errorsByType.entries())
              .sort((a, b) => b[1] - a[1])[0];
            
            notifyError(
              `All ${results.total} transaction(s) failed to save to pending. ` +
              `Most common: ${topError[1]}× "${topError[0].substring(0, 50)}${topError[0].length > 50 ? '...' : ''}". ` +
              `${results.deleted} deleted, ${results.rejected} rejected. See error report below.`
            );
            
            setBatchError(new BatchPartialError(
              `All transactions failed`,
              results.errors
            ));
          }
        }
      }
    } catch (error) {
      console.error('Failed to save to pending', error);
      setBatchError(error);
    } finally {
      setLoading({ saveToPending: false });
    }
  };

  const handleValidation = async () => {
    setLoading({ validate: true });
    setBatchError(null);

    try {
      const records = rows.length;
      if (records > 0) {
        const rowsCopy = [...rows];

        // Generate external_id for each transaction that doesn't have one
        rowsCopy.forEach((row) => {
          if (!row.externalId) {
            row.externalId = generateExternalIdForTransaction(row);
          }
        });

        if (blockOnMetadataErrors(rowsCopy)) {
          return;
        }

        await Promise.all(rowsCopy.map(createBatchRequest));

        const res = await batchValidateTransactions(
          listReq,
          client,
          activeUser
        );

        // Aggregated results tracking
        const results = {
          total: rowsCopy.length,
          successCount: 0,
          failureCount: 0,
          errors: [], // Limited to first 1000 errors
          errorsByType: new Map(), // Group by error message
        };
        const MAX_STORED_ERRORS = 1000;

        // Process each error in the returned list
        res.forEach((errorMsg, index) => {
          if (errorMsg && errorMsg.trim() !== '') {
            // Non-empty error message - validation failed for this index
            results.failureCount++;

            const errorKey = errorMsg.trim();
            results.errorsByType.set(
              errorKey,
              (results.errorsByType.get(errorKey) || 0) + 1
            );

            if (results.errors.length < MAX_STORED_ERRORS) {
              results.errors.push({
                index: index + 1,
                status: 'Validation Failed',
                errorMessage: errorKey,
                accountNo: rowsCopy[index]?.accountNo,
                contraAccountNo: rowsCopy[index]?.contraAccountNo,
                symbol: rowsCopy[index]?.symbol,
                cusip: rowsCopy[index]?.cusip,
                externalId: rowsCopy[index]?.externalId,
              });
            }
          } else {
            // Empty error message - validation passed for this index
            results.successCount++;
          }
        });

        // Show aggregated summary notification and set batch error based on results
        if (results.failureCount === 0) {
          notifySuccess(
            `✓ Validation passed for all ${results.total} transaction(s).`
          );
          setBatchError(null);
        } else if (results.successCount > 0) {
          // Partial success - show top 2 error types
          const topErrors = Array.from(results.errorsByType.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 2)
            .map(([msg, count]) => `${count}× "${msg.substring(0, 40)}${msg.length > 40 ? '...' : ''}"`)
            .join(', ');

          notifyError(
            `Validation: ${results.successCount}/${results.total} passed, ` +
            `${results.failureCount} failed. ` +
            `Common errors: ${topErrors}. See details below.`
          );

          setBatchError(new BatchPartialError(
            `${results.failureCount} transaction(s) failed validation`,
            results.errors
          ));
        } else {
          // Total failure
          const topError = Array.from(results.errorsByType.entries())
            .sort((a, b) => b[1] - a[1])[0];

          notifyError(
            `Validation failed for all ${results.total} transaction(s). ` +
            `Most common: ${topError[1]}× "${topError[0].substring(0, 50)}${topError[0].length > 50 ? '...' : ''}". ` +
            `See error report below.`
          );

          setBatchError(new BatchPartialError(
            `All transactions failed validation`,
            results.errors
          ));
        }
      }
    } catch (error) {
      console.error('Failed to validate', error);
      setBatchError(error);
    } finally {
      setLoading({ validate: false });
    }
  };

  const createBatchRequest = async (data, key) => {
    listReq.push(data);
  };

  const findMetadataErrors = (rowsToSubmit) =>
    rowsToSubmit.flatMap((row, index) =>
      getEntryTypeMetadataErrors(
        row.entryType,
        row.entrySubtype,
        row.metadata
      ).map((message) => ({
        index: index + 1,
        status: 'Rejected',
        errorMessage: message,
        accountNo: row.accountNo,
        contraAccountNo: row.contraAccountNo,
        symbol: row.symbol,
        cusip: row.cusip,
        externalId: row.externalId,
      }))
    );

  const blockOnMetadataErrors = (rowsToSubmit) => {
    const metadataErrors = findMetadataErrors(rowsToSubmit);
    if (metadataErrors.length === 0) {
      return false;
    }

    notifyError(
      `${metadataErrors.length} of ${rowsToSubmit.length} transaction(s) have missing or invalid metadata. See error report below.`
    );
    setBatchError(
      new BatchPartialError(
        `${metadataErrors.length} transaction(s) have missing or invalid metadata`,
        metadataErrors
      )
    );
    return true;
  };

  const handleDelete = (rowsToDelete) => {
    let messageKey = '';
    if (rowsToDelete.length === 1) {
      messageKey = rows[rowsToDelete[0].dataIndex].correspondent;
    } else {
      messageKey = rowsToDelete.length + ' items';
    }

    confirm({
      description:
        "You are about to delete '" +
        messageKey +
        "'. Please confirm your action.",
      confirmationText: 'Yes',
    }).then(async () => {
      const rowsCopy = [...rows];
      try {
        for (const r of rowsToDelete) {
          let index = r.dataIndex;
          rowsCopy.splice(index, 1);
        }

        setRows(rowsCopy);
        notifySuccess(messageKey + ' Entry has been removed');
      } catch (error) {
        console.error(error);
      }
    });
  };

  const handleOpen = (data) => {
    setRowData(data);
    setEditRecord(true);
  };

  const handleEditClose = async (data) => {
    if (data === undefined) {
      setEditRecord(false);
      return;
    }

    if (!data.entryType) {
      notifyError('Entry Type is required.');
      return;
    }
    if (!data.accountNo) {
      notifyError('Account No is required.');
      return;
    }
    if (!data.contraAccountNo) {
      notifyError('Contra Account is required.');
      return;
    }

    if (checkEntryType(data.entryType) === 'trd') {
      if (!data.side) {
        notifyError('Side is required.');
        return;
      }
      if (!data.tradeDate) {
        notifyError('Trade Date is required.');
        return;
      }
    }
    if (
      checkEntryType(data.entryType) === 'trd' ||
      checkEntryType(data.entryType) === 'pm' ||
      checkEntryType(data.entryType) === 'cpm'
    ) {
      if (!data.symbol) {
        notifyError('Symbol is required.');
        return;
      }
    }
    if (
      checkEntryType(data.entryType) === 'trd' ||
      checkEntryType(data.entryType) === 'pm'
    ) {
      if (!data.qty) {
        notifyError('Qty is required.');
        return;
      }
      if (!data.price) {
        notifyError('Price is required.');
        return;
      }
    }
    if (checkEntryType(data.entryType) === 'cpm') {
      if (!data.netAmt && !data.localNetAmt) {
        notifyError('Net Amt or Local Net Amt is required.');
        return;
      }
    }
    if (
      (data.contraSymbol || 'USD') !== 'USD' &&
        ENTRY_TYPES_AUTOFILL_BLACKLIST.includes(data.entryType)
    ) {
      if (!data.swapRate) {
        notifyError('Swap Rate is required for non-USD transactions.');
        return;
      }
    }

    try {
      const rowsCopy = [...rows];
      const index = rows.indexOf(rowData);
      data.error = false;
      rowsCopy[index] = data;
      setRows(rowsCopy);
      notifySuccess('Record has been updated.');
    } catch (error) {
      console.error(error);
    }
    setEditRecord(false);
  };

  return (
    <div className={classes.root}>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modalBackdrop}
        open={isOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={isOpen}>
          <div className={classes.tableModalFull}>
            <div
              className={classes.grdRow}
              style={{ margin: 0, padding: '25px 30px 25px 30px' }}
            >
              <div className={classes.grdCell1}>
                <EntryTypeSelect
                  freeSolo={false}
                  name="entryType"
                  label="Entry Type"
                  value={entryType}
                  onChange={(e) => setEntryType(e.currentTarget.value)}
                  allowInputChange={true}
                />
              </div>
              <div className={classes.grdCell1}></div>
              <div
                className={classes.grdCellNone}
                style={{ marginRight: 10, marginTop: 3 }}
              >
                <label htmlFor="raised-button-file">
                  <Button
                    component="span"
                    className={classes.button}
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<AttachFile />}
                  >
                    Choose File
                  </Button>
                </label>
                <input
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                  className={classes.input}
                  style={{ display: 'none' }}
                  id="raised-button-file"
                  type="file"
                  onChange={(e) => {
                    readExcel(e.target.files[0]);
                    e.target.value = null;
                  }}
                />
              </div>
              <div
                className={classes.grdCellNone}
                style={{ marginTop: 3, marginRight: 10 }}
              >
                <Tooltip
                  title={
                    !rows.every((item) => item.error === false)
                      ? 'Cannot execute while there are missing required value(s)'
                      : 'Execute'
                  }
                >
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={
                        !rows.length ||
                        loading.execute ||
                        loading.saveToPending ||
                        loading.validate ||
                        !rows.every((item) => item.error === false)
                      }
                      startIcon={
                        loading.execute ? (
                          <CircularProgress
                            style={{ color: '#ffffff', height: 20, width: 20 }}
                          />
                        ) : null
                      }
                      onClick={() => {
                        handleExecution();
                      }}
                    >
                      {loading.execute ? 'Executing...' : 'Execute'}
                    </Button>
                  </div>
                </Tooltip>
              </div>
              <div className={classes.grdCellNone} style={{ marginTop: 3, marginRight: 10 }}>
                <Tooltip
                  title={
                    !rows.every((item) => item.error === false)
                      ? 'Cannot execute while there are missing required value(s)'
                      : 'Save to Pending'
                  }
                >
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={
                        !rows.length ||
                        loading.execute ||
                        loading.saveToPending ||
                        loading.validate ||
                        !rows.every((item) => item.error === false)
                      }
                      startIcon={
                        loading.saveToPending ? (
                          <CircularProgress
                            style={{ color: '#ffffff', height: 20, width: 20 }}
                          />
                        ) : null
                      }
                      onClick={() => {
                        handlePendingExecution();
                      }}
                    >
                      {loading.saveToPending ? 'Saving...' : 'Save to Pending'}
                    </Button>
                  </div>
                </Tooltip>
              </div>
              <div className={classes.grdCellNone} style={{ marginTop: 3 }}>
                <Tooltip
                  title={
                    !rows.every((item) => item.error === false)
                      ? 'Cannot validate while there are missing required value(s)'
                      : 'Validate'
                  }
                >
                  <div>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      disabled={
                        !rows.length ||
                        loading.execute ||
                        loading.saveToPending ||
                        loading.validate ||
                        !rows.every((item) => item.error === false)
                      }
                      startIcon={
                        loading.validate ? (
                          <CircularProgress
                            style={{ color: '#ffffff', height: 20, width: 20 }}
                          />
                        ) : null
                      }
                      onClick={() => {
                        handleValidation();
                      }}
                    >
                      {loading.validate ? 'Validating...' : 'Validate'}
                    </Button>
                  </div>
                </Tooltip>
              </div>
            </div>
            <div style={{ color: 'red', paddingLeft: '25px' }}>
              {batchError instanceof BatchPartialError ? (
                <BatchErrorLines batchError={batchError} />
              ) : (
                batchError?.message
              )}
            </div>
            <Box component="div">
              <TransactionTable
                entrytype={entryType}
                data={previewRows}
                editData={rowData}
                isEdit={editRecord}
                onEditOpen={handleOpen}
                onEditClose={handleEditClose}
                onRowDelete={handleDelete}
              ></TransactionTable>
            </Box>
            {rows.length >= PREVIEW_ROW_LIMIT && (
              <p
                style={{
                  textAlign: 'end',
                  paddingRight: '12px',
                  paddingBottom: '12px',
                  fontStyle: 'italic',
                }}
              >
                Preview only displaying first 10,000 entries
              </p>
            )}
            <div className={classes.modalCloseIcon}>
              <IconButton aria-label="close" onClick={handleClose}>
                <CloseIcon style={{ color: '#f1f1f1' }} />
              </IconButton>
            </div>
          </div>
        </Fade>
      </Modal>
    </div>
  );
}

export function downloadCSV(filename, data) {
  // Extract headers with more comprehensive error details
  const headers = [
    'Row',
    'Status',
    'Error Message',
    'Account No',
    'Contra Account No',
    'Symbol',
    'Cusip',
    'External ID',
  ];

  // Convert rows to CSV text
  const csvRows = [
    headers.join(','),
    ...data.map((row) => {
      const fields = [
        row.index || '',
        row.status || '',
        row.errorMessage || '',
        row.accountNo || '',
        row.contraAccountNo || '',
        row.symbol || '',
        row.cusip || '',
        row.externalId || '',
      ];
      
      return fields
        .map((value) => {
          const escaped = String(value).replace(/"/g, '""');
          return `"${escaped}"`;
        })
        .join(',');
    }),
  ];

  // Add BOM so Excel reads UTF-8 correctly
  const csvString = '\uFEFF' + csvRows.join('\n');

  // Blob with UTF-8 + BOM
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  // Download with timestamp in filename
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
  const downloadFilename = filename.replace('.csv', `-${timestamp}.csv`);
  
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', downloadFilename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
